/**
 * How long the embedding WordPress page gets to act on `iframeLocation` before
 * the booking app navigates the top window itself. A page that handles it
 * starts navigating at once, and this iframe then gets `beforeunload`, which
 * cancels the fallback. `pagehide` comes only when Stripe's page commits, which
 * a slow connection can delay past this; iOS Safari may skip `beforeunload`,
 * hence the margin.
 */
export const CHECKOUT_HANDOFF_FALLBACK_MS = 6000;

/** 'attempted': the top navigation was started from here; 'blocked': the browser refused it. */
export type TopNavigationOutcome = 'attempted' | 'blocked';

export interface CheckoutHandoffOptions {
  /** The host page ignored the hand-off; offer a target="_top" link to the same URL. */
  onFallback: (outcome: TopNavigationOutcome, error?: unknown) => void;
  fallbackMs?: number;
  /** Injectable for unit tests. */
  win?: Window;
}

/**
 * Send the customer to Stripe Checkout.
 *
 * Opened directly (not embedded), navigate this window. Embedded in a
 * WordPress page, post the `iframeLocation` message so the host navigates the
 * whole page. A host page can drop it: on 2026-10-07 /votre-soiree-approche/
 * only resized the iframe, and every noon invite hung on "Paiement". So if the
 * host has not started navigating after `fallbackMs`, navigate the top window
 * from here — browsers allow it for a non-sandboxed cross-origin iframe the
 * user has interacted with, and they just clicked "Paiement" — and tell the
 * caller, so it can show a link in case the browser refused. Returns a cancel
 * function.
 */
export function handOffToCheckout(url: string, options: CheckoutHandoffOptions): () => void {
  const win = options.win ?? window;
  if (win.top === win.self) {
    win.location.assign(url);
    return () => {};
  }

  win.parent.postMessage({ action: 'iframeLocation', url }, '*');

  let timer = 0;
  const cancel = () => {
    win.clearTimeout(timer);
    win.removeEventListener('beforeunload', cancel);
    win.removeEventListener('pagehide', cancel);
  };
  timer = win.setTimeout(() => {
    cancel();
    try {
      win.top!.location.href = url;
      options.onFallback('attempted');
    } catch (error) {
      options.onFallback('blocked', error);
    }
  }, options.fallbackMs ?? CHECKOUT_HANDOFF_FALLBACK_MS);
  // The host started navigating (`beforeunload`), or this page is going away
  // (`pagehide`, also on entering the back/forward cache): never fire later.
  win.addEventListener('beforeunload', cancel);
  win.addEventListener('pagehide', cancel);
  return cancel;
}
